'use client';

import * as React from 'react';

// Last-resort boundary for errors thrown in the ROOT layout itself (above every
// segment error.tsx). It replaces the whole document, so it must render its own
// <html>/<body> and can't rely on the app's CSS variables being present. Kept
// deliberately minimal: a plain reload is the only safe recovery this high up.
// The common transient cases (network blips inside the inbox) are handled by
// the self-healing (inbox)/error.tsx; this only fires for rarer layout-level
// failures.

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): React.JSX.Element {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('[root] global error:', error);
    const onOnline = (): void => reset();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [error, reset]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: '#E9F1F3',
          color: '#1D2C3A',
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div style={{ maxWidth: 360, textAlign: 'center', padding: 24 }}>
          <h1 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600 }}>
            Something went wrong
          </h1>
          <p
            style={{
              margin: '0 0 24px',
              fontSize: 14,
              lineHeight: 1.5,
              color: 'color-mix(in oklch, #1D2C3A 62%, #E9F1F3)',
            }}
          >
            The app hit an unexpected error. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
            style={{
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              color: '#E9F1F3',
              background: '#FF4D00',
              border: 0,
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      </body>
    </html>
  );
}
